/* ap-geo.js — Australian-visitor personalization (Alkaline Superfoods).
 * Detects AU visitors client-side (no paid geo service), marks the document with
 * `.geo-au`, exposes window.ALK_GEO, and swaps the US-specific copy/logos for
 * Australian equivalents. Everyone else keeps the shipped US experience.
 *
 * Detection order (first hit wins):
 *   1. ?geo=au / ?geo=us        — manual override, sticky (for QA + campaign links)
 *   2. localStorage alk_geo     — cached result, 7-day TTL
 *   3. window.Shopify.country   — Shopify's own localization value
 *   4. Intl timeZone "Australia/*" — cheap, offline, surprisingly reliable
 *   5. /browsing_context_suggestions.json — Shopify's detected country (async)
 *
 * Everything is wrapped in try/catch: on any failure the page stays as shipped.
 */
(function () {
  'use strict';

  var KEY = 'alk_geo';
  var TTL = 7 * 24 * 60 * 60 * 1000;
  var A = window.ALK_GEO_ASSETS || {};
  var applied = false;

  function readCache() {
    try {
      var r = JSON.parse(localStorage.getItem(KEY) || 'null');
      if (r && r.c && (new Date().getTime() - r.t) < TTL) return r.c;
    } catch (e) { /* storage blocked */ }
    return null;
  }

  function writeCache(c) {
    try { localStorage.setItem(KEY, JSON.stringify({ c: c, t: new Date().getTime() })); } catch (e) {}
  }

  function param() {
    var m = /[?&]geo=([a-zA-Z]{2})/.exec(location.search);
    return m ? m[1].toUpperCase() : null;
  }

  function syncDetect() {
    var p = param();
    if (p) { writeCache(p); return p; }
    var c = readCache();
    if (c) return c;
    try {
      if (window.Shopify && Shopify.country) return String(Shopify.country).toUpperCase();
    } catch (e) {}
    try {
      var tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      if (tz.indexOf('Australia/') === 0) return 'AU';
    } catch (e) {}
    return null;
  }

  function asyncDetect() {
    return fetch('/browsing_context_suggestions.json', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        var c = j && j.detected_values && j.detected_values.country && j.detected_values.country.handle;
        return c ? String(c).toUpperCase() : null;
      })
      .catch(function () { return null; });
  }

  // ───────────────────────── DOM swaps ─────────────────────────

  function each(sel, fn) {
    Array.prototype.forEach.call(document.querySelectorAll(sel), fn);
  }

  // Replace US phrasings inside an element's own text nodes (leaves markup alone).
  //
  // Rules are ordered most-specific first and are CASE-SENSITIVE so the
  // replacement keeps the casing of the copy it lands in: the all-caps rule
  // rewrites the announcement bar ("FINAL U.S. STOCK"), the sentence-case rule
  // rewrites body copy ("Final U.S. stock only.") — shouting inside a paragraph
  // was the old behaviour and it looked broken.
  //
  // NOTE: nothing here may match "American Express" — that is a card brand, not
  // a US-only claim, and it must survive untouched in the payment-icon labels.
  var PHRASES = [
    // ── carrier / origin lines ────────────────────────────────────────────
    [/by\s+UPS\s+from\s+the\s+U\.?S\.?/gi, 'by Australia Post from Australia'],
    [/Ships?\s+from\s+U\.?S\.?\s+(?:inventory\s+)?by\s+UPS/gi, 'Ships from Australia by Australia Post'],
    [/Ships?\s+from\s+U\.?S\.?\s+inventory\s+by/gi, 'Ships from Australia by'],
    [/Ships?\s+from\s+U\.?S\.?(?![A-Za-z])/gi, 'Ships from Australia'],

    // ── stock / inventory claims (all-caps variants first) ────────────────
    [/FINAL\s+U\.?S\.?\s+STOCK/g, 'FINAL AUSTRALIAN STOCK'],
    [/Final\s+U\.?S\.?\s+stock/g, 'Final Australian stock'],
    [/final\s+U\.?S\.?\s+stock/g, 'final Australian stock'],
    [/\bU\.?S\.?\s+FINAL\s+STOCK/g, 'FINAL AUSTRALIAN STOCK'],
    [/\bU\.?S\.?\s+INVENTORY\s+ONLY/g, 'AUSTRALIAN WAREHOUSE STOCK'],
    [/\bU\.?S\.?\s+inventory\s+only/gi, 'Australian warehouse stock'],
    [/\bU\.?S\.?\s+supplement\s+stock/gi, 'Australian supplement stock'],
    [/\bU\.?S\.?\s+STOCK/g, 'AUSTRALIAN STOCK'],
    [/\bU\.?S\.?\s+stock/g, 'Australian stock'],
    [/\bU\.?S\.?\s+INVENTORY/g, 'AUSTRALIAN INVENTORY'],
    [/\bU\.?S\.?\s+inventory/g, 'Australian inventory'],
    [/\bU\.?S\.?\s+RANGE/g, 'AUSTRALIAN RANGE'],
    [/\bU\.?S\.?\s+range/g, 'Australian range'],
    [/\bU\.?S\.?\s+warehouses?/gi, 'Australian warehouse'],

    // ── shipping promises ─────────────────────────────────────────────────
    [/FREE\s+U\.?S\.?\s+SHIPPING/g, 'FREE SHIPPING'],
    [/Free\s+U\.?S\.?\s+Shipping/g, 'Free Shipping'],
    [/Free\s+U\.?S\.?\s+shipping/g, 'Free shipping'],
    [/free\s+U\.?S\.?\s+shipping/g, 'free shipping'],

    // ── bare "the U.S." / "in the US" left over in prose ───────────────────
    [/the\s+U\.S\.(?![A-Za-z])/g, 'Australia'],

    [/🇺🇸/g, '🇦🇺'] // 🇺🇸 → 🇦🇺
  ];

  // Text inside these never gets rewritten.
  var SKIP_TAGS = { SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEXTAREA: 1, CODE: 1, PRE: 1, TEMPLATE: 1 };

  function skipNode(node) {
    for (var p = node.parentNode; p && p.nodeType === 1; p = p.parentNode) {
      if (SKIP_TAGS[p.nodeName]) return true;
      if (p.hasAttribute && p.hasAttribute('data-alk-geo-skip')) return true;
    }
    return false;
  }

  function swapText(root) {
    if (!root) return;
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null, false);
    var node, changed;
    while ((node = walker.nextNode())) {
      var t = node.nodeValue;
      // Cheap pre-filter: every rule needs an "S"/"s" or the US-flag surrogate.
      if (!t || (t.indexOf('S') === -1 && t.indexOf('s') === -1 && t.indexOf('\uD83C') === -1)) continue;
      if (skipNode(node)) continue;
      changed = t;
      for (var i = 0; i < PHRASES.length; i++) changed = changed.replace(PHRASES[i][0], PHRASES[i][1]);
      if (changed !== t) node.nodeValue = changed;
    }
  }

  // 1 + 2a. Hero copy + UPS → Australia Post
  function swapHero() {
    each('.ap-hero__badge, .ap-hero__trust, .ap-hero__headline', function (el) { swapText(el); });
    var badge = document.querySelector('.ap-hero__badge');
    if (badge && !/AUSTRALIAN/i.test(badge.textContent)) badge.textContent = 'FINAL AUSTRALIAN STOCK';
    var trust = document.querySelector('.ap-hero__trust');
    if (trust) {
      var spans = trust.querySelectorAll('span');
      if (spans.length && !/AUSTRALIAN/i.test(spans[0].textContent)) spans[0].textContent = 'FINAL AUSTRALIAN STOCK.';
    }
  }

  // 2a. Every UPS logo becomes the Australia Post mark.
  //
  // Two real crops of the official artwork ship with the theme:
  //   ap-auspost-logo.png      red mark + wordmark on transparent — light surfaces
  //   ap-auspost-logo-dark.png white wordmark on the red brand banner — dark hero
  // Picking per-surface avoids the white-chip-on-a-dark-photo look.
  function swapCarrierLogos() {
    if (!A.auspost) return;
    each('img[src*="ap-ups-logo"]', function (img) {
      var onDark = !!img.closest('.ap-hero, .ap-hero__trust') || img.classList.contains('ap-hero__trust-ups');
      var src = (onDark && A.auspostDark) ? A.auspostDark : A.auspost;
      img.setAttribute('src', src);
      img.setAttribute('srcset', '');
      img.removeAttribute('width');
      img.removeAttribute('height');
      if (img.getAttribute('alt')) img.setAttribute('alt', 'Australia Post');
      img.classList.add('alk-auspost-logo');
      if (onDark && A.auspostDark) img.classList.add('alk-auspost-logo--dark');
      img.style.width = '';
      img.style.height = '';
    });
  }

  // 2b. US flag emoji in trust lines → Australian flag asset + AU warehouse copy.
  function swapTrustLines() {
    // Sweep the whole page rather than a hand-maintained selector list — every
    // round of QA turned up one more tile the list had missed (the product-page
    // "U.S. stock" icon row was the last one). PHRASES is conservative enough
    // to run document-wide, and the mutation observer keeps re-rendered
    // sections (cart drawer, exit popup, section reloads) covered.
    swapText(document.body);
    if (!A.flagAu) return;
    each('.alkaline-product-trustline, .ap-pairings__trustline', function (el) {
      if (el.querySelector('.alk-flag-au')) return;
      var html = el.innerHTML.replace(/🇦🇺|🇺🇸/g,
        '<img class="alk-flag-au" src="' + A.flagAu + '" alt="Australia" width="480" height="240" loading="lazy">');
      if (html !== el.innerHTML) el.innerHTML = html;
    });
  }

  // 3. Zelle / Venmo are US-only rails — hide those badges, retitle the labels.
  function swapPayments() {
    each('.alkaline-payment-icons', function (ul) {
      Array.prototype.forEach.call(ul.children, function (li) {
        if (/zelle|venmo/i.test(li.textContent)) li.classList.add('alk-us-only-pay');
      });
      var label = ul.getAttribute('aria-label');
      if (label && /zelle|venmo/i.test(label)) {
        ul.setAttribute('aria-label', label
          .replace(/,?\s*Zelle\s+and\s+Venmo/gi, '')
          .replace(/Zelle,\s*Venmo,?\s*(and|or)?\s*/gi, '')
          .replace(/Venmo,\s*Zelle,?\s*(and|or)?\s*/gi, '')
          .replace(/\s{2,}/g, ' ').trim());
      }
    });
    // Payment-method tiles that name the US rails directly.
    each('.alkaline-product-icon span, .alkaline-product-trust-strip p, .alkaline-trust-strip p', function (el) {
      var t = el.textContent.trim();
      if (/^Zelle\s*(\/|or|,|and)\s*Venmo$/i.test(t) || /^Venmo\s*(\/|or|,|and)\s*Zelle$/i.test(t)) {
        el.textContent = 'Card or Bank Transfer';
      }
    });
  }

  // 4. Currency suffix — AU visitors see which currency they are actually charged in.
  function currencySuffix() {
    var cur = null;
    try { cur = window.Shopify && Shopify.currency && Shopify.currency.active; } catch (e) {}
    cur = (cur || 'USD').toUpperCase();
    // If the theme is already printing an ISO code (money_with_currency), stay out
    // of the way — doubling it up would read "$84.95USD USD".
    var sample = document.querySelector('.price .price-item, .ap-card-price, .ap-products__price, .totals__total-value');
    if (sample && /[A-Z]{3}\s*$/.test(sample.textContent.trim())) return;
    document.documentElement.style.setProperty('--alk-cur-suffix', '" ' + cur + '"');
  }

  function runSwaps() {
    try { swapHero(); } catch (e) {}
    try { swapCarrierLogos(); } catch (e) {}
    try { swapTrustLines(); } catch (e) {}
    try { swapPayments(); } catch (e) {}
    try { currencySuffix(); } catch (e) {}
  }

  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  function apply(country) {
    window.ALK_GEO = country || null;
    var au = country === 'AU';
    var root = document.documentElement;
    root.classList.toggle('geo-au', au);
    root.setAttribute('data-alk-geo', country || '');
    onReady(function () {
      if (document.body) document.body.classList.toggle('geo-au', au);
      if (!au || applied) return;
      applied = true;
      runSwaps();
      // Cart drawer / section renders replace markup — re-apply, debounced.
      var t = null;
      var obs = new MutationObserver(function () {
        if (t) clearTimeout(t);
        t = setTimeout(function () { obs.disconnect(); runSwaps(); obs.observe(document.body, OPTS); }, 120);
      });
      var OPTS = { childList: true, subtree: true };
      obs.observe(document.body, OPTS);
      ['cart:refresh', 'cart-drawer:open', 'shopify:section:load'].forEach(function (ev) {
        document.addEventListener(ev, runSwaps);
      });
    });
  }

  var sync = syncDetect();
  if (sync) {
    writeCache(sync);
    apply(sync);
  } else {
    apply(null);
    asyncDetect().then(function (c) {
      if (!c) return;
      writeCache(c);
      apply(c);
    });
  }
})();
